import axios, { AxiosResponse } from 'axios';
import { config } from 'process';
import React, { useContext, useEffect, useState } from 'react';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { DriverType } from '../../../../interfaces/delivery-company/drivers';
import {
  CompactMerchantType,
  MerchantType,
} from '../../../../interfaces/delivery-company/merchants';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Popover,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  Typography,
  TablePagination,
  Alert,
  Button,
  LinearProgress,
  Chip,
  Box,
  Grid,
} from '@mui/material';
import {
  DeliveryState,
  PaymentState,
  Workflow,
} from '../../../../enums/orders';
import AutocompleteWithThrottle from '../../../common/autocomplete-with-throttle/autocomplete-with-throttle';
import { OrderType, ExtraChargeSuggestions, InvoiceFrequencySuggestionDetails, ExtraCharge } from '../../../../interfaces/delivery-company/orders';
import { ExtraChargeReason, ExtraChargeReasonLabels, InvoiceFrequencyPeriodTypeLabels } from '../../../../enums/extra-charges';
import { displayMoney } from '../../../../utils/money';
import {
  ActorFilterOption,
  DeliveryStateFilterOption,
  FilterKey,
  IFilterOption,
  PaymentStateFilterOption,
} from '../../../common/filter-bar/types';
import { useImmer } from 'use-immer';
import {
  DoubleDate,
  DoubleDateMode,
  DoubleDateWithMode,
} from '../../../common/double-date-picker/double-date-picker.interfaces';
import { PaginationContext } from '../../../../contexts/pagination-context';
import { buildQueryParams } from '../../../common/filter-bar/filter-bar.utils';
import DoubleDatePicker from '../../../common/double-date-picker/double-date-picker';
import FilterBar from '../../../common/filter-bar/filter-bar';
import SearchBar from '../../../common/search-bar/search-bar';
import { searchOptions } from '../../customers/customers.config';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { isMobile, isTablet } from 'react-device-detect';
import { ErrorsFlattenner } from '../../../common/errors-flattenner/errors-flattenner';
import GenericTable from '../../../common/generic-table/generic-table';
import {
  displayLocation,
  displayMultipleLocations,
} from '../../../../utils/locations';
import { showDeliveryState } from '../../../common/styles/order-states.style';
import orders from '../../orders/orders';
import { PickupTaskState } from '../../../../enums/tasks';
import { IPickupTask } from '../../../../interfaces/delivery-company/tasks';
import { format } from 'date-fns';
import { ColumnConfig } from '../../../common/generic-table/utils';
import { showWorkflow } from '../../../common/styles/workflow-display';
import DoubleDateOrdersFilter from '../../../common/double-date-orders-filter/double-date-orders-filter';
import { generateDateFilterQueryParam } from '../../../common/double-date-orders-filter/utils';
import { Checklist } from '@mui/icons-material';
import OrderNotes from '../../../common/order-notes/order-notes';
import IncompleteAddressError from '../../../common/incomplete-address-error/incomplete-address-error';
import { IMerchantOrdersInvoice, InvoicePaymentMethod } from '../../../../interfaces/common/merchant-invoices';
import { IDriverInvoice } from '../../../../interfaces/common/driver-invoices';
import MerchantAmountsSummary from './merchant-amounts-summary';
import { get_cod } from '../../../../utils/cod';
import { ShopLocationType } from '../../../../interfaces/delivery-company/pickups';
import FilterChips from '../../../common/filter-chips/filter-chips';
import { useSnackBar } from '../../../../contexts/snackbar';
import PaymentMethodSelector from '../../../common/payment-method-selector/payment-method-selector';

export type Target = OrderType | IPickupTask;

interface CreateNewInvoiceProps {
  fixed_filters_query: string;
  filter_keys: FilterKey[];
  mode: 'pickup' | 'delivery';
  create_invoice_callback: (items: Target[], extraCharges?: ExtraCharge[], paymentMethod?: InvoicePaymentMethod) => Promise<AxiosResponse>;
  onSuccess: () => void;
  merchant?: MerchantType;
}

function CreateNewInvoice({
  fixed_filters_query,
  create_invoice_callback,
  filter_keys,
  mode,
  onSuccess, 
  merchant,
}: CreateNewInvoiceProps) {
  const [itemsList, setItemsList] = useState<Target[]>();

  const [itemsCount, setIemsCount] = useState<number>(0);
  const [message, setMessage] = useState('');
  const [response, setResponse] = useState<AxiosResponse>();
  const [filters, setFilters] = useState<IFilterOption[]>([]);
  
  const [availablePaymentLocations, setAvailablePaymentLocations] = useState<ShopLocationType[]>();
  const [selectedPaymentLocations, setSelectedPaymentLocations] = useState<ShopLocationType[]>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<InvoicePaymentMethod | null>(null);

  const [extraCharges, setExtraCharges] = useState<ExtraCharge[]>([]);
  const [extraChargeSuggestions, setExtraChargeSuggestions] = useState<ExtraChargeSuggestions | null>(null);
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(new Set());
  const [loadingSuggestions, setLoadingSuggestions] = useState<boolean>(false);
  const [suggestionsLoaded, setSuggestionsLoaded] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCreatingInvoice, setIsCreatingInvoice] = useState<boolean>(false);
  const [selectedItems, updateSelectedItems] = useImmer<Map<string, Target>>(
    new Map()
  );
  const [dateFilter, setDateFilter] = useState<DoubleDateWithMode>({
    startDate: null,
    endDate: null,
    mode: DoubleDateMode.DELIVERY_DATE,
  });

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: 1000,
    searchColumn: mode == 'delivery' ? 'Order_Id' : 'Task_Id',
    searchValue: '',
    searchOperation: 'equals',
  });

  const totalSelected = selectedItems.size;

  const { user, config } = useContext(AuthContext);
  const {showMessage} = useSnackBar();

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const fetchShopLocationWithPayableOrders = async () => {
    if (merchant) {
      const endpoint = `${API_ENDPOINT}/api/v1/pickups/?page_size=10&merchant=${merchant.id}&payable_to_merchant=true`;

      try {
        const response = await axios.get<{
          count: number;
          results: ShopLocationType[];
        }>(endpoint, config);

        console.log(response);

        return response.data.results;
      } catch (error) {
        console.log(error);
      }
    }
    return [];
  };

  useEffect(() => {
    const getPayableLocations = async () => {
      if (merchant) {
        setAvailablePaymentLocations(await fetchShopLocationWithPayableOrders());
      }
    }
    if (merchant) {
      getPayableLocations();
    }
  }, [merchant])

  useEffect(() => {
    const fetchExtraChargeSuggestions = async () => {
      if (merchant && mode === 'delivery') {
        setLoadingSuggestions(true);
        setSuggestionsLoaded(false);
        try {
          const endpoint = `${API_ENDPOINT}/api/v1/merchant/${merchant.id}/invoice-extra-charge-suggestion/`;
          const response = await axios.get<ExtraChargeSuggestions>(endpoint, config);
          setExtraChargeSuggestions(response.data);
          setDismissedSuggestions(new Set());
          setSuggestionsLoaded(true);
        } catch (error) {
          console.error('Failed to fetch extra charge suggestions:', error);
          setSuggestionsLoaded(true); // Mark as loaded even on error so user can proceed
        } finally {
          setLoadingSuggestions(false);
        }
      } else {
        // No merchant or not delivery mode, mark as loaded
        setSuggestionsLoaded(true);
      }
    };

    fetchExtraChargeSuggestions();
  }, [merchant, mode])

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setController({
      ...controller,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };
  const resetPaginationControls = () => {
    setController({
      ...controller,
      page: 0,
    });
  };

  const resetSelectionState = () => {
    updateSelectedItems((selectedItems) => {
      selectedItems.clear();
    });
  };

  const filterQueryAction = (filters: IFilterOption[]) => {
    resetPaginationControls();
    setFilters(filters);
    resetSelectionState();
  };

  const dateFilterAction = (dateFilterValue: DoubleDateWithMode) => {
    resetPaginationControls();
    setDateFilter(dateFilterValue);
  };

  const resetAll = () => {
    resetPaginationControls();
    resetSelectionState();
  };

  useEffect(() => {
    let response: any;
    const getData = async () => {
      let relevant_fields: string[] = [];
      if (mode == 'delivery') {
        relevant_fields = [
          'id',
          'order_id',
          'reference_id',
          'creation_date',
          'merchant',
          'merchant_location',
          'customer',
          'customer_location',
          'driver',
          'price',
          'labels',
          'pop',
          'delivery_fee',
          'driver_commission',
          'workflow',
          'number_of_packages',
          'delivery_state',
          'payment_state',
          'branch',
          'tracking_hash',
          'description',
          'return_reason',
          'public_note',
          'delivery_fee_covered_by_merchant',
          'exchange_for'
        ];
      } else {
        relevant_fields = [
          'id',
          'status',
          'driver',
          'driver_commission',
          'package_count',
          'creation_date',
          'workflow',
          'merchant_location',
          'customer_location',
        ];
      }

      let endpoint = `${API_ENDPOINT}/api/v1/${mode === 'delivery' ? 'orders' : 'pickup-tasks'}/?page=${controller.page + 1}&page_size=${controller.rowsPerPage}&fields=${relevant_fields.join(',')}`;

      const filterQueryParams = buildQueryParams(filters);
      endpoint += `${filterQueryParams}`;
      endpoint += `&${generateDateFilterQueryParam(dateFilter)}`;
      endpoint += `&${fixed_filters_query}`;

      if (selectedPaymentLocations.length > 0) {
        endpoint += `&merchant_location=${selectedPaymentLocations.map(location => location.id).join(',')}`;
      }

      try {
        response = await axios.get<{ count: number; results: Target[] }>(
          endpoint,
          config
        );

        setItemsList(response.data.results);
        setIemsCount(response.data.count);
      } catch (error) {
        console.log(error);
      }
      setIsLoading(false);
    };
    setIsLoading(true);
    getData();
    console.log(response);
  }, [dateFilter, filters, JSON.stringify(controller), selectedPaymentLocations]);

  // Check if there are pending suggestions (neither applied nor dismissed)
  const hasPendingSuggestions = extraChargeSuggestions && Object.entries(extraChargeSuggestions).some(
    ([reason, suggestion]) => {
      if (!suggestion.should_apply) return false;
      const isApplied = extraCharges.some(charge => charge.reason === reason);
      const isDismissed = dismissedSuggestions.has(reason);
      return !isApplied && !isDismissed;
    }
  );

  const handleGenerateInvoice = async () => {
    setIsLoading(true);
    setIsCreatingInvoice(true);
    setResponse(undefined);
    setMessage('');
    try {
      if (!selectedPaymentMethod) {

        showMessage("Please choose a payment method.", "error")
        setIsLoading(false);
        setIsCreatingInvoice(false);
      }
      const response = await create_invoice_callback(
        Array.from(selectedItems.values()),
        extraCharges.length > 0 ? extraCharges : undefined,
        selectedPaymentMethod!
      );
      onSuccess();
      setResponse(response);
      setMessage('Invoice created successfully');
    } catch (e) {
      let message = 'Failed to create invoice';
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        message = `${message}: ${e.response?.statusText}`;
      }
      setMessage(message);
    } finally {
      setIsLoading(false);
      setIsCreatingInvoice(false);
    }
  };

  const handleAcceptSuggestion = (reason: string, suggestion: any) => {
    // Only add if not already in the list
    if (!extraCharges.some(charge => charge.reason === reason)) {
      setExtraCharges([
        ...extraCharges,
        {
          reason: reason,
          amount: suggestion.charge_amount,
        },
      ]);
    }
  };

  const handleDismissSuggestion = (reason: string) => {
    setDismissedSuggestions(new Set([...Array.from(dismissedSuggestions), reason]));
  };

  return (
    <div>
      <div style={{ width: '100%', textAlign: 'center' }}>
        <Button
          size={(isMobile && !isTablet) ? 'small' : 'medium'}
          disabled={totalSelected === 0 || hasPendingSuggestions || isLoading || !suggestionsLoaded || (mode === 'delivery' && !selectedPaymentMethod)}
          variant="contained"
          onClick={handleGenerateInvoice}
        >
          {loadingSuggestions ? 'Loading...' : 'Create Invoice'}
        </Button>
        {loadingSuggestions && totalSelected > 0 && (
          <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 1 }}>
            Loading extra charge suggestions...
          </Typography>
        )}
        {hasPendingSuggestions && totalSelected > 0 && !loadingSuggestions && (
          <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
            Please apply or skip all extra charge suggestions below
          </Typography>
        )}
      </div>
      <br />

      <Card sx={{ padding: 2 }} elevation={0}>
        {message !== '' && (
          <>
            <Alert
              severity={
                response && response.status >= 200 && response.status < 300
                  ? 'success'
                  : 'error'
              }
            >
              <>
                {message}

                {response && response.status >= 300 && (
                  <ErrorsFlattenner rawErrors={response.data} />
                )}
              </>
            </Alert>

            <br />
          </>
        )}

        {/* Extra Charge Suggestions */}
        {extraChargeSuggestions && Object.entries(extraChargeSuggestions).map(([reason, suggestion]) => {
          if (!suggestion.should_apply) return null;
          
          const isApplied = extraCharges.some(charge => charge.reason === reason);
          const isDismissed = dismissedSuggestions.has(reason) && !isApplied;
          const selection = isApplied ? 'apply' : isDismissed ? 'skip' : null;
          
          return (
            <div key={reason}>
              <Alert
                severity={selection ? 'success' : 'warning'}
                sx={{ 
                  border: selection ? '2px solid' : '1px solid',
                  borderColor: selection ? 'success.main' : 'warning.main',
                  maxWidth: 500
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                    {reason === ExtraChargeReason.INVOICE_FREQUENCY_EXCEEDED && (
                      <>
                        Extra charge of <strong>{displayMoney(suggestion.charge_amount)}</strong> suggested because this merchant has received {(suggestion as InvoiceFrequencySuggestionDetails).count_in_period} invoices during the current {InvoiceFrequencyPeriodTypeLabels[(suggestion as InvoiceFrequencySuggestionDetails).period_type as keyof typeof InvoiceFrequencyPeriodTypeLabels] || (suggestion as InvoiceFrequencySuggestionDetails).period_type.toLowerCase().replace('_', ' ')} (limit: {(suggestion as InvoiceFrequencySuggestionDetails).limit}).
                      </>
                    )}
                    {reason !== ExtraChargeReason.INVOICE_FREQUENCY_EXCEEDED && (
                      <>
                        Extra charge of <strong>{displayMoney(suggestion.charge_amount)}</strong> suggested for {ExtraChargeReasonLabels[reason as keyof typeof ExtraChargeReasonLabels] || reason}.
                      </>
                    )}
                  </Typography>
                  
                  <RadioGroup
                    value={selection}
                    onChange={(e) => {
                      if (isCreatingInvoice) return; // Prevent changes while creating invoice
                      
                      const value = e.target.value;
                      if (value === 'apply') {
                        // Remove from dismissed if it was there
                        const newDismissed = new Set(dismissedSuggestions);
                        newDismissed.delete(reason);
                        setDismissedSuggestions(newDismissed);
                        
                        // Add to charges if not already there
                        if (!isApplied) {
                          handleAcceptSuggestion(reason, suggestion);
                        }
                      } else if (value === 'skip') {
                        // Remove from charges if it was there
                        const chargeIndex = extraCharges.findIndex(charge => charge.reason === reason);
                        if (chargeIndex !== -1) {
                          setExtraCharges(extraCharges.filter((_, i) => i !== chargeIndex));
                        }
                        // Add to dismissed
                        handleDismissSuggestion(reason);
                      }
                    }}
                    sx={{ flexDirection: 'row', gap: 3 }}
                  >
                    <FormControlLabel
                      value="apply"
                      control={<Radio disabled={isCreatingInvoice} />}
                      label={
                        <Typography variant="body2" color={isCreatingInvoice ? 'text.disabled' : 'text.primary'}>
                          <strong>Apply charge</strong>
                        </Typography>
                      }
                      disabled={isCreatingInvoice}
                    />
                    <FormControlLabel
                      value="skip"
                      control={<Radio disabled={isCreatingInvoice} />}
                      label={
                        <Typography variant="body2" color={isCreatingInvoice ? 'text.disabled' : 'text.primary'}>
                          <strong>Skip (do not apply)</strong>
                        </Typography>
                      }
                      disabled={isCreatingInvoice}
                    />
                  </RadioGroup>
                </Box>
              </Alert>
              <br />
            </div>
          );
        })}


        <Accordion elevation={0}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Typography
              variant="h5"
              component="h5"
              sx={{ textAlign: 'center', mt: 1, mb: 1 }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                <ManageSearchIcon fontSize="large" color="primary" />
                <span>
                  <b>Search and Filter Controls</b>
                </span>
              </div>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <DoubleDateOrdersFilter dateFilterAction={dateFilterAction} />
            <br />

            {filter_keys.length > 0 && (
              <FilterBar
                filterQueryAction={filterQueryAction}
                currentlySelectedFilters={filters}
                filterKeys={filter_keys}
              />
            )}
          </AccordionDetails>
        </Accordion>

        {isLoading ? (
          <LinearProgress />
        ) : (
          <>
            {itemsList ? (
              <>
                 <Box display={"flex"} width={"100%"} justifyContent={"space-between"} alignItems={"center"} sx={{mb:2}}>

                 <Chip
                  icon={<Checklist />}
                  color="primary"
                  label={`Selected ${totalSelected}/${itemsCount}`}
                  variant={
                    totalSelected === itemsCount && totalSelected > 0
                      ? 'filled'
                      : 'outlined'
                  }
                />
                
                {selectedItems.size > 0 && <>
                  {mode == "delivery" ? <MerchantAmountsSummary orders={Array.from(selectedItems.values()) as OrderType[]} extraCharges={extraCharges} /> :
                    <> </>
                  }

                </>}

              </Box>

              <Grid container spacing={2} mb={2}>
                {mode === 'delivery' && (
                  <Grid item xs={12} md={availablePaymentLocations && availablePaymentLocations.length > 0 ? 6 : 12}>
                    <PaymentMethodSelector
                      value={selectedPaymentMethod}
                      onChange={(method) => {
                        setSelectedPaymentMethod(method);
                      }}
                      label="Select payment method"
                    />
                  </Grid>
                )}
                
                {availablePaymentLocations && availablePaymentLocations.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <FilterChips 
                      centered
                      multiple
                      label={'Filter orders by merchant branch'}
                      options={availablePaymentLocations}
                      selected={selectedPaymentLocations}
                      getOptionLabel={(option) => `${option.name}: ${displayLocation(option)}`}
                      onChange={(selected) => {
                        setSelectedPaymentLocations(selected);
                      }}
                    />
                  </Grid>
                )}
              </Grid>
              
                <GenericTable<Target>
                  sourceView="create-new-orders-invoice"
                  items={itemsList}
                  selectedItems={selectedItems}
                  updateSelectedItems={updateSelectedItems}
                  isLoading={isLoading}
                  fullWidth
                  selectionEnabled
                  columns={
                    (mode == 'delivery'
                      ? [
                          {
                            id: 'order_id',
                            header: 'Order ID',
                            display_body: (order: OrderType) => (
                              <a href={`/order/${order.id}`}>
                              <b>
                                {order.order_id}
                                {order.reference_id && order.reference_id.length > 0 && (
                                  <>
                                    <br />({order.reference_id})
                                  </>
                                )}{' '}
                              </b>
                            </a>
                            ),
                          },
                          {
                            id: 'merchant',
                            header: 'Merchant',
                            display_body: (order: OrderType) => (
                              <>
                                {order.merchant?.name}
                                <br />{' '}
                                {displayLocation(order.merchant_location)}
                              </>
                            ),
                          },
                          {
                            id: 'customer',
                            header: 'Customer',
                            display_body: (order: OrderType) => (
                              <>
                                {order.customer?.name}
                                <br />{' '}
                                {displayLocation(order.customer_location)}
                              </>
                            ),
                          },
                          {
                            id: 'notes',
                            header: 'Notes',
                            display_body: (order: OrderType) => (
                             <>
                              {!order.customer_location?.area && <IncompleteAddressError /> }
                              <OrderNotes order={order} />
                            </>
                            ),
                          },
                          {
                            id: 'workflow',
                            header: 'Workflow',
                            display_body: (order: OrderType) => (
                              <>
                                {showWorkflow(
                                  order.workflow,
                                  order.is_exchange,
                                  order.exchange_for,
                                  order.return_reason
                                )}
                              </>
                            ),
                          },
                          {
                            id: 'delivery state',
                            header: 'Status',
                            display_body: (order: OrderType) =>
                              showDeliveryState(order.delivery_state),
                          },
                           {
                            id: 'COD',
                            header: 'COD / Return Fees',
                            display_body: (order: OrderType) => displayMoney(get_cod(order))
                          },
                          {
                            id: 'delivery_charge',
                            header: 'Delivery Fees',
                            display_body: (order: OrderType) => {
                              if(order.workflow == Workflow.RETURN) return '-'
                              return displayMoney(order.delivery_fee)
                            }
                          },
                        ]
                      : [
                          {
                            id: 'task_id',
                            header: <p>Task ID</p>,
                            display_body: (task: IPickupTask) => (
                              <b>
                                <u>{task.id}</u>
                              </b>
                            ),
                          },
                          {
                            id: 'creation_date',
                            header: <p>Created At</p>,
                            display_body: (task: IPickupTask) => (
                              <p>
                                {format(
                                  new Date(task.creation_date ?? ''),
                                  'dd-MM-yyyy'
                                )}

                                <br />
                                {format(
                                  new Date(task.creation_date ?? ''),
                                  'HH:mm:ss'
                                )}
                              </p>
                            ),
                          },
                          {
                            id: 'workflow',
                            header: <p>Workflow</p>,
                            display_body: (task: IPickupTask) => (
                              <p>{showWorkflow(task.workflow, false,undefined, "")}</p>
                            ),
                          },
                          {
                            id: 'Pickup From',
                            header: <p>Pickup From</p>,
                            display_body: (task: IPickupTask) => {
                              if (task.workflow === Workflow.STANDARD)
                                return (
                                  <>
                                    Merchant <br />
                                    {task.merchant?.name} <br />{' '}
                                    {displayLocation(task.merchant_location)}{' '}
                                  </>
                                );
                              else
                                return (
                                  <>
                                    Customer <br />
                                    {task.customer?.name} <br />{' '}
                                    {displayLocation(task.customer_location)}{' '}
                                  </>
                                );
                            },
                          },
                          {
                            id: 'package count',
                            header: <p># Packages</p>,
                            display_body: (task: IPickupTask) => (
                              <p>{task.package_count}</p>
                            ),
                          },
                          {
                            id: 'task_state',
                            header: <p>Status</p>,
                            display_body: (task: IPickupTask) => (
                              <>{task.status} </>
                            ),
                          },
                        ]) as ColumnConfig<Target>[]
                  }
                  getItemUID={(order) => order.id}
                />

                <TablePagination
                  component="div"
                  onPageChange={handlePageChange}
                  page={controller.page}
                  count={itemsCount}
                  rowsPerPage={controller.rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 250, 500, 1000]}
                  labelRowsPerPage=""
                  style={{ overflow: 'visible' }}
                />
              </>
            ) : (
              <Alert severity="error">Failed to load orders</Alert>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

export default CreateNewInvoice;
